import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppNav from '../components/common/AppNav';
import WorldMap from '../components/WorldMap';
import '../styles/public.css';

const GLOBE_HINT_KEY = 'nomadme_globe_hint_seen';

type Region = 'africa' | 'china' | 'world';

const REGION_CONFIG: Record<Region, {
  center: [number, number];
  zoom: number;
  label: string;
  badge: string;
  minZoom: number;
  maxBounds?: [[number, number], [number, number]];
}> = {
  africa: {
    center: [20, 2],
    zoom: 2.5,
    label: 'Africa',
    badge: 'africa',
    minZoom: 2.5,
    maxBounds: [[-30, -42], [60, 42]],
  },
  china: {
    center: [104, 34],
    zoom: 3.0,
    label: 'China',
    badge: 'china',
    minZoom: 3.0,
    maxBounds: [[65, 10], [145, 60]],
  },
  world: {
    center: [20, 15],
    zoom: 1.6,
    label: 'World',
    badge: 'world',
    minZoom: 1,
    maxBounds: undefined,
  },
};

const GlobePage: React.FC = () => {
  const { t } = useTranslation('landing');
  const [params] = useSearchParams();
  const regionKey = (params.get('region') ?? 'world') as Region;
  const region = REGION_CONFIG[regionKey] ?? REGION_CONFIG.world;

  const [hintDismissed, setHintDismissed] = useState(() => {
    try { return localStorage.getItem(GLOBE_HINT_KEY) === '1'; } catch { return false; }
  });
  const [legendExpanded, setLegendExpanded] = useState(false);

  const MARKER_LEGEND = [
    { icon: '🏪', label: t('globePage.legendBoutiques') },
    { icon: '🧭', label: t('globePage.legendJourneys') },
    { icon: '📦', label: t('globePage.legendCargo') },
  ];

  const dismissHint = () => {
    try { localStorage.setItem(GLOBE_HINT_KEY, '1'); } catch {}
    setHintDismissed(true);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#0a0c14' }}>
      <AppNav accentColor="#38bdf8" />

      {/* Full-viewport flex column — gives WorldMap's flex:1 root real height */}
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
        <WorldMap
          key={regionKey}
          initialCenter={region.center}
          initialZoom={region.zoom}
          regionMinZoom={region.minZoom}
          maxBounds={region.maxBounds}
          isDark
        />
      </div>

      {/* Region badge — sits below the 60px AppNav, overlays the map */}
      <div className="globe-page-header">
        <span>{t('globePage.header')}</span>
        <span className={`globe-region-badge globe-region-badge--${region.badge}`}>
          {region.label}
        </span>
      </div>

      {/* First-visit hint overlay */}
      {!hintDismissed && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(10,12,20,0.7)',
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          animation: 'globe-hint-in 0.4s ease',
        }}>
          <div style={{
            background: 'rgba(15,20,35,0.95)',
            border: '1px solid rgba(56,189,248,0.25)',
            borderRadius: 16,
            padding: '28px 32px',
            maxWidth: 340,
            textAlign: 'center',
            boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 10 }}>🌍</div>
            <h3 style={{ color: '#f1f5f9', fontSize: '1.1rem', fontWeight: 800, margin: '0 0 8px' }}>
              {t('globePage.hintTitle')}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '0.85rem', lineHeight: 1.6, margin: '0 0 20px' }}>
              {t('globePage.hintDesc')}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 22 }}>
              {MARKER_LEGEND.map((m) => (
                <div key={m.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: '1.4rem' }}>{m.icon}</span>
                  <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</span>
                </div>
              ))}
            </div>
            <button
              onClick={dismissHint}
              style={{
                background: 'linear-gradient(135deg,#38bdf8,#0ea5e9)',
                border: 'none',
                borderRadius: 8,
                color: '#0a0c14',
                fontWeight: 800,
                fontSize: '0.88rem',
                padding: '9px 24px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              {t('globePage.hintCta')}
            </button>
          </div>
        </div>
      )}

      {/* Persistent mini-legend chip (bottom corner) */}
      <div style={{
        position: 'fixed',
        bottom: 20,
        right: 16,
        zIndex: 30,
      }}>
        {legendExpanded ? (
          <div style={{
            background: 'rgba(10,12,20,0.9)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 12,
            padding: '10px 14px',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            flexDirection: 'column',
            gap: 7,
          }}>
            {MARKER_LEGEND.map((m) => (
              <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.9rem', width: 20, textAlign: 'center' }}>{m.icon}</span>
                <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 600 }}>{m.label}</span>
              </div>
            ))}
            <button
              onClick={() => setLegendExpanded(false)}
              style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: '0.7rem', textAlign: 'right', padding: '2px 0 0' }}
            >
              {t('globePage.legendClose')}
            </button>
          </div>
        ) : (
          <button
            onClick={() => setLegendExpanded(true)}
            title="Map legend"
            style={{
              background: 'rgba(10,12,20,0.85)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              color: '#64748b',
              fontSize: '0.72rem',
              fontWeight: 700,
              padding: '5px 10px',
              cursor: 'pointer',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              letterSpacing: '0.04em',
            }}
          >
            {t('globePage.legendOpen')} ▲
          </button>
        )}
      </div>
    </div>
  );
};

export default GlobePage;
